import { observer } from "mobx-react";
import { ChangeOfAddressForm, PreviousAddress, ZipItem } from "./changeOfAddressDomain";
import { Box, VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { MTextInput } from "@/components-v2/TextInput";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { MCheckboxGroup } from "@/components-v2/Checkbox";
import { useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { getAllCountriesSortedByName, getCountryName } from "@/utils/countries";
import { Language } from "@/i18n/languages";
import { compact } from "lodash";
import { Markdown } from "@/text/Markdown";

function formatPreviousAddress(previous: PreviousAddress, language: Language): string {
  const zipCity = compact([previous.zip, previous.city]).join(" ");
  const country = previous.country
    ? (getCountryName(previous.country, language) ?? previous.country)
    : undefined;
  return compact([previous.addressLine1, previous.addressLine2, zipCity, country]).join(", ");
}

const MunicipalityAlertInfo = observer(function MunicipalityWarning({
  form,
}: {
  form: ChangeOfAddressForm;
}) {
  const { t } = useTranslation();
  return (
    <Markdown
      value={t(
        form.context.isBatchProject
          ? "decisions.changeOfAddress.batch-info"
          : "decisions.changeOfAddress.municipality-alert",
      )}
      enableLinks={!form.context.isBatchProject}
      inheritLinkColor={!form.context.isBatchProject}
      disableLastMargin={!form.context.isBatchProject}
      onActionClick={
        !form.context.isBatchProject ? form.addChangeArticlesOfAssociationDecision : undefined
      }
    />
  );
});

export const ChangeOfAddressView = observer(function ChangeOfAddressView({
  form,
}: {
  form: ChangeOfAddressForm;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const disabled = form.isProjectLocked;

  const countries = getAllCountriesSortedByName(language);
  const selectedCountryCode = form.country.inputValue;
  const selectedCountry = selectedCountryCode
    ? (countries.find((c) => c.code === selectedCountryCode) ?? {
        code: selectedCountryCode,
        name: getCountryName(selectedCountryCode, language) ?? selectedCountryCode,
      })
    : null;
  const selectedMailingCountryCode = form.mailingCountry.inputValue;
  const selectedMailingCountry = selectedMailingCountryCode
    ? (countries.find((c) => c.code === selectedMailingCountryCode) ?? {
        code: selectedMailingCountryCode,
        name: getCountryName(selectedMailingCountryCode, language) ?? selectedMailingCountryCode,
      })
    : null;

  const selectedZip: ZipItem | null = form.zip.inputValue
    ? { zip: form.zip.inputValue, city: form.city.inputValue }
    : null;
  const selectedMailingZip: ZipItem | null = form.mailingZip.inputValue
    ? { zip: form.mailingZip.inputValue, city: form.mailingCity.inputValue }
    : null;

  const previousAddressText = form.previousAddress
    ? formatPreviousAddress(form.previousAddress, language)
    : "";
  const previousMailingAddressText = form.previousMailingAddress
    ? formatPreviousAddress(form.previousMailingAddress, language)
    : "";

  return (
    <VBox gap={"md"}>
      <VBox gap={"sm"}>
        <strong>{t("decisions.changeOfAddress.what-do-you-want-to-do")}</strong>
        <MCheckboxGroup model={form.changeOptions} disabled={disabled} />
      </VBox>
      <Alert type="info" icon="warning" message={<MunicipalityAlertInfo form={form} />} />
      {form.changesBusinessAddress && (
        <>
          {previousAddressText && (
            <Box>
              <span className={"subtle"}>{t("decisions.changeOfAddress.current-address")}</span>{" "}
              <span>{previousAddressText}</span>
            </Box>
          )}
          <VBox marginTop>
            <strong>{t("decisions.changeOfAddress.new-address")}</strong>
          </VBox>
          <MTextInput
            model={form.addressLine1}
            label={t("decisions.changeOfAddress.addressLine1")}
            disabled={disabled}
          />
          <MTextInput
            model={form.addressLine2}
            label={t("decisions.changeOfAddress.addressLine2")}
            disabled={disabled}
          />
          <Autocomplete
            label={t("decisions.changeOfAddress.zip")}
            disabled={disabled}
            items={form.zipItems(form.zip.inputValue)}
            value={selectedZip}
            inputValue={form.zip.inputValue}
            onInputValueChange={form.zip.setAndCommitInputValue}
            renderInputValue={(item) => item.zip}
            onSelect={form.selectZip}
            onClear={() => form.zip.setAndCommitInputValue("")}
            idFn={(item) => item.zip}
            labelFn={(item) => `${item.zip} ${item.city}`}
          />
          <Autocomplete
            label={t("decisions.changeOfAddress.city")}
            disabled={disabled}
            items={form.cityItems(form.city.inputValue)}
            value={form.city.inputValue || null}
            inputValue={form.city.inputValue}
            onInputValueChange={form.city.setAndCommitInputValue}
            onSelect={form.selectCity}
            onClear={() => form.city.setAndCommitInputValue("")}
            idFn={(item) => item}
            labelFn={(item) => item}
          />
          <Autocomplete
            label={t("decisions.changeOfAddress.country")}
            disabled={disabled}
            items={countries}
            value={selectedCountry}
            onSelect={(item) => form.selectCountry(item.code)}
            onClear={() => form.selectCountry("")}
            idFn={(item) => item.code}
            labelFn={(item) => item.name}
          />
        </>
      )}
      {form.changesMailingAddress && (
        <>
          {previousMailingAddressText && (
            <Box>
              <span className={"subtle"}>{t("decisions.changeOfAddress.current-address")}</span>{" "}
              <span>{previousMailingAddressText}</span>
            </Box>
          )}
          <VBox marginTop>
            <strong>{t("decisions.changeOfAddress.new-mailing-address")}</strong>
          </VBox>
          <Autocomplete
            label={t("decisions.changeOfAddress.country")}
            disabled={disabled}
            items={countries}
            value={selectedMailingCountry}
            onSelect={(item) => form.selectMailingCountry(item.code)}
            onClear={() => form.selectMailingCountry("")}
            idFn={(item) => item.code}
            labelFn={(item) => item.name}
          />
          <MTextInput
            model={form.mailingAddressLine1}
            label={t("decisions.changeOfAddress.addressLine1")}
            disabled={disabled}
          />
          <MTextInput
            model={form.mailingAddressLine2}
            label={t("decisions.changeOfAddress.addressLine2")}
            disabled={disabled}
          />
          {form.usesNorwegianMailingAddressLookup ? (
            <>
              <Autocomplete
                label={t("decisions.changeOfAddress.zip")}
                disabled={disabled}
                items={form.zipItems(form.mailingZip.inputValue)}
                value={selectedMailingZip}
                inputValue={form.mailingZip.inputValue}
                onInputValueChange={form.mailingZip.setAndCommitInputValue}
                onSelect={form.selectMailingZip}
                onClear={() => form.mailingZip.setAndCommitInputValue("")}
                idFn={(item) => item.zip}
                labelFn={(item) => `${item.zip} ${item.city}`}
              />
              <Autocomplete
                label={t("decisions.changeOfAddress.city")}
                disabled={disabled}
                items={form.cityItems(form.mailingCity.inputValue)}
                value={form.mailingCity.inputValue || null}
                inputValue={form.mailingCity.inputValue}
                onInputValueChange={form.mailingCity.setAndCommitInputValue}
                onSelect={form.selectMailingCity}
                onClear={() => form.mailingCity.setAndCommitInputValue("")}
                idFn={(item) => item}
                labelFn={(item) => item}
              />
            </>
          ) : (
            <>
              <MTextInput
                model={form.mailingZip}
                label={t("decisions.changeOfAddress.zip")}
                disabled={disabled}
              />
              <MTextInput
                model={form.mailingCity}
                label={t("decisions.changeOfAddress.city")}
                disabled={disabled}
              />
            </>
          )}
        </>
      )}
    </VBox>
  );
});
